import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import type { Schema } from './schema.generated';
import type { TestTrack } from './testTrack';
import type { SplitName, VariantName } from '@betterment-oss/test-track';

type FeatureFlagConfig<S extends SplitName<Schema>> = {
  splitName: S;
  context: string;
  trueVariant?: VariantName<Schema, S>;
};

const TestTrackContext = createContext<Promise<TestTrack> | null>(null);

export const TestTrackProvider = TestTrackContext.Provider;

export function useTestTrack(): TestTrack | null {
  const testTrackPromise = useContext(TestTrackContext);
  if (!testTrackPromise) throw new Error('useTestTrack must be used within a TestTrackProvider');

  const [testTrack, setTestTrack] = useState<TestTrack | null>(null);
  useEffect(() => void testTrackPromise.then(setTestTrack), [testTrackPromise]);

  return testTrack;
}

export function useFeatureFlag<S extends SplitName<Schema>>({ splitName, context, trueVariant }: FeatureFlagConfig<S>) {
  const testTrack = useTestTrack();

  return useMemo(() => {
    if (!testTrack) {
      return { loading: true, enabled: false };
    }

    return {
      loading: false,
      enabled: testTrack.ab(splitName, { context, trueVariant }),
    };
  }, [testTrack, context, splitName, trueVariant]);
}
